import { furthestOf } from '../analytics';
import { deriveStatus } from '../core/status';
import { GUIDED_JOURNEY_ID } from '../registry';
import { TourState } from '../types';

/** Shape of the old, single-tour guidedTour slice we are migrating away from. */
export interface LegacyGuidedTourState {
  isActive?: boolean;
  isCompleted?: boolean;
  stepHistory?: string[];
  bannerFirstShownAt?: number;
  mobileWarningDismissed?: boolean;
  /** Set when a user session last owned this persisted slice. */
  userId?: string;
}

export interface LegacyMigrationPlan {
  /** Seed into `tours.guidedJourney` when present. */
  tourState?: TourState;
  /** Carry the dismiss flag into the new tours slice. */
  mobileWarningDismissed?: boolean;
  /** Always clear the legacy slice once a logged-in session has considered it. */
  shouldClearLegacy: boolean;
}

/**
 * Decide what to do with rehydrated legacy `guidedTour` state for the current user.
 *
 * Must run only after `sessionChecked && logged` so we know whose session we are seeding.
 * A legacy `userId` that does not match the current user is cleared without migrating, so a
 * shared browser cannot leak onboarding progress across accounts.
 *
 * TODO(sc-6875): Remove with TourLegacyMigration after one release.
 */
export const planLegacyGuidedTourMigration = ({
  legacy,
  existingGuidedJourney,
  currentUserId,
}: {
  legacy?: LegacyGuidedTourState | null;
  existingGuidedJourney?: TourState;
  currentUserId: string;
}): LegacyMigrationPlan => {
  if (!legacy) return { shouldClearLegacy: false };

  // Wrong account: drop the stale slice, do not seed.
  if (legacy.userId && legacy.userId !== currentUserId) {
    return { shouldClearLegacy: true };
  }

  const plan: LegacyMigrationPlan = { shouldClearLegacy: true };

  if (legacy.mobileWarningDismissed) {
    plan.mobileWarningDismissed = true;
  }

  if (!legacy.stepHistory?.length) return plan;
  // Already have real progress in the new slice — keep it, still clear legacy.
  if (existingGuidedJourney && deriveStatus(existingGuidedJourney) !== 'notStarted') return plan;

  const now = new Date().toISOString();
  const furthest = furthestOf(GUIDED_JOURNEY_ID, legacy.stepHistory);
  plan.tourState = {
    stepHistory: legacy.stepHistory,
    furthestStepId: furthest.furthestStepId,
    furthestStepNumber: furthest.furthestStepIndex >= 0 ? furthest.furthestStepIndex : undefined,
    startedAt: legacy.bannerFirstShownAt ? new Date(legacy.bannerFirstShownAt).toISOString() : undefined,
    completedTimes: legacy.isCompleted ? 1 : 0,
    ...(legacy.isCompleted && { completedAt: now, lastCompletedAt: now }),
  };

  return plan;
};

export default planLegacyGuidedTourMigration;
